/**
 * Cashier session + CSRF, behind the pwa-auth-gateway.
 *
 * MIGRATION (Rook, 2026-08-07): this file used to store a raw JWT in IndexedDB and hand it to
 * every request as `Authorization: Bearer`. That model died when the cashier gateway went up:
 * the gateway owns an httpOnly `cashier_session` cookie, translates it to a Bearer downstream,
 * and enforces a double-submit CSRF check on every state-changing request. The old model could
 * not authenticate against it at all.
 *
 * WHY THE OLD FUNCTIONS ARE GONE RATHER THAN LEFT UNUSED: hop's service worker kept reading a
 * `jwt` key that nothing had written since its own migration. It always read null, sent no auth
 * header, and the resulting 403 was swallowed for weeks. DEAD CREDENTIAL CODE IS NOT HARMLESS —
 * it reads as a working auth path to everyone who greps for one.
 *
 * The session token is httpOnly and unreadable here, by design. The CSRF value is a nonce the
 * gateway deliberately issues NON-httpOnly precisely so page JS can echo it back in a header.
 */
import { openCashierDb, STORES } from './db'
import { CASHIER_ROLE } from '../types'

export interface SessionUser {
  id: string
  username: string
  roles: string[]
}

export interface SessionState {
  authenticated: boolean
  user?: SessionUser
  /** R413: the owner set this person's password; they must choose their own first. */
  mustChangePassword?: boolean
}

const XSRF_COOKIE_NAME = 'XSRF-TOKEN'
export const WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

/**
 * Same-origin gateway prefix. NOT configurable — a build-time URL is exactly what broke this
 * app (see api.ts). Lives here, not in api.ts, because offlineQueue.ts needs it too and
 * api.ts already imports offlineQueue: putting it there would close an import cycle.
 */
export const API_PREFIX = '/api'

/** The header set the gateway requires. The session cookie rides along automatically; the
 *  double-submit CSRF nonce does not, and its absence is a silent 403 on every write. */
export function requestHeaders(method: string, json: boolean): Record<string, string> {
  const headers: Record<string, string> = {}
  if (json) headers['Content-Type'] = 'application/json'
  if (WRITE_METHODS.has(method.toUpperCase())) headers['X-XSRF-TOKEN'] = getCSRFToken()
  return headers
}

/** Key the service worker reads for the CSRF nonce. sw.js MUST agree with this. */
export const CSRF_KEY = 'csrf'

/** R413: key the service worker reads for the queue's owner (the session user's id). sw.js
 *  MUST agree with this. The till is shared: the worker sends only this person's requests. */
export const SUB_KEY = 'sub'

/** R413: the gateway's 403 code for a person who must choose their own password first. */
export const PASSWORD_CHANGE_REQUIRED = 'PASSWORD_CHANGE_REQUIRED'

/** R413: the window event App listens for to show the change-password screen. */
export const PASSWORD_CHANGE_REQUIRED_EVENT = 'cashier:password-change-required'

let _session: SessionState | null = null
let _sessionPromise: Promise<SessionState> | null = null
let _ownerChain: Promise<void> = Promise.resolve()

async function writeOwner(sub: string | null): Promise<void> {
  try {
    const db = await openCashierDb()
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORES.AUTH, 'readwrite')
      const store = tx.objectStore(STORES.AUTH)
      if (sub) store.put(sub, SUB_KEY)
      else store.delete(SUB_KEY)
      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error)
    })
  } catch {
    /* non-fatal: with no owner stored the worker sends nothing */
  }
}

/** Writes (or clears) the worker's stored owner. Chained, so writes land in call order. */
function queueOwnerWrite(sub: string | null): Promise<void> {
  _ownerChain = _ownerChain.then(() => writeOwner(sub))
  return _ownerChain
}

/** R413: clears the worker's stored owner (logout, 401, and just before a login POST). */
export function clearWorkerOwner(): Promise<void> {
  return queueOwnerWrite(null)
}

/** R413: the signed-in person's id, or undefined when nobody is signed in. */
export function currentSub(): string | undefined {
  return _session?.user?.id || undefined
}

/** R413: true only when the gateway answered a literal `mustChangePassword: true`. */
export function sessionMustChangePassword(): boolean {
  return _session?.mustChangePassword === true
}

/**
 * R413: reads a 403's JSON body from a clone and says whether it is the gateway's
 * must-change-password refusal. A missing clone or an unreadable body is simply "no".
 */
export async function isPasswordChangeRequired(res: Response): Promise<boolean> {
  if (res.status !== 403) return false
  try {
    const body = (await res.clone().json()) as { error?: { code?: unknown } } | null
    return !!body && typeof body.error === 'object' && body.error !== null && body.error.code === PASSWORD_CHANGE_REQUIRED
  } catch {
    return false
  }
}

/** R413: tells App to show the change-password screen. */
export function announcePasswordChangeRequired(): void {
  window.dispatchEvent(new Event(PASSWORD_CHANGE_REQUIRED_EVENT))
}

/** Reads the gateway's non-httpOnly double-submit CSRF cookie. */
export function getCSRFToken(): string {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${XSRF_COOKIE_NAME}=([^;]+)`))
  return match ? decodeURIComponent(match[1]) : ''
}

/**
 * Hand the current CSRF nonce to the service worker.
 *
 * IndexedDB is not a convenience here — it is the only channel that reaches the worker. A
 * ServiceWorker has no `document`, so it cannot read the cookie, and background sync runs
 * precisely when the page is CLOSED, so there is no client to postMessage.
 *
 * Best-effort: a failure must never break the page. The foreground path reads the cookie
 * directly and does not depend on this.
 */
export async function persistCsrfForWorker(token?: string): Promise<void> {
  const value = token ?? getCSRFToken()
  if (!value) return
  try {
    const db = await openCashierDb()
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORES.AUTH, 'readwrite')
      tx.objectStore(STORES.AUTH).put(value, CSRF_KEY)
      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error)
    })
  } catch {
    /* non-fatal — see above */
  }
}

async function fetchSession(): Promise<SessionState> {
  try {
    const res = await fetch('/auth/session', { headers: { Accept: 'application/json' } })
    if (!res.ok) return { authenticated: false }
    const data = (await res.json()) as {
      authenticated?: boolean
      user?: SessionUser
      mustChangePassword?: unknown
    }
    return data.authenticated && data.user
      ? { authenticated: true, user: data.user, mustChangePassword: data.mustChangePassword === true }
      : { authenticated: false }
  } catch {
    return { authenticated: false }
  }
}

/**
 * Two-tab fix (Frank #63294): the user id the browser's CURRENT cookie names, read from
 * GET /auth/session. Another tab may have signed someone else in since this tab last
 * read its session. Only a 200 with authenticated: true and a non-empty user.id counts;
 * anything else (network error, non-ok, bad body) is null. Read-only: it never adopts
 * the session and never writes or clears the stored owner or csrf.
 */
export async function readLiveSessionUserId(): Promise<string | null> {
  try {
    const res = await fetch('/auth/session', { headers: { Accept: 'application/json' } })
    if (!res.ok) return null
    const data = (await res.json()) as { authenticated?: unknown; user?: { id?: unknown } } | null
    if (!data || data.authenticated !== true || !data.user) return null
    const id = data.user.id
    return typeof id === 'string' && id !== '' ? id : null
  } catch {
    return null
  }
}

/** Bootstrap — fetches (and memoizes) the session once per tab. */
export async function initializeSession(): Promise<SessionState> {
  if (_session) return _session
  if (!_sessionPromise) _sessionPromise = fetchSession()
  _session = await _sessionPromise
  if (_session.authenticated) void persistCsrfForWorker()
  void queueOwnerWrite(_session.user?.id ?? null)
  return _session
}

/** Force a re-fetch — after login, or when a 401 says the cached state is stale. */
export async function refreshSession(): Promise<SessionState> {
  _sessionPromise = fetchSession()
  _session = await _sessionPromise
  // ROTATION: re-auth can mint a NEW nonce. Re-persisting is what stops a stale worker copy
  // from silently 403-ing every background replay.
  if (_session.authenticated) void persistCsrfForWorker()
  // R413: the worker's owner follows the session: this user's id, or none.
  void queueOwnerWrite(_session.user?.id ?? null)
  return _session
}

/** Roles come from the gateway's session response — never decoded from a token here. */
export function sessionRoles(): string[] {
  return _session?.user?.roles ?? []
}

export async function hasPosAccess(): Promise<boolean> {
  const s = await initializeSession()
  return !!s.authenticated && (s.user?.roles ?? []).includes(CASHIER_ROLE)
}

/** Clears the gateway session; the httpOnly cookie can only be dropped server-side. */
export async function logout(): Promise<void> {
  void clearWorkerOwner()
  try {
    await fetch('/auth/logout', { method: 'POST', headers: { 'X-XSRF-TOKEN': getCSRFToken() } })
  } catch {
    /* best-effort */
  }
  _session = null
  _sessionPromise = null
}

export function _resetForTest(): void {
  _session = null
  _sessionPromise = null
  _ownerChain = Promise.resolve()
}

/** Tests: resolves once every owner write queued so far has landed. */
export function _ownerWritesForTest(): Promise<void> {
  return _ownerChain
}
